import { DownloadRecordType } from '@drift/common';
import {
	CurrentPerpMarkets,
	CurrentSpotMarkets,
} from 'src/environmentVariables/EnvironmentVariables';
import useDriftActions from 'src/hooks/useDriftActions';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import { useEffect, useMemo, useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import Button from './Button';
import Select from './Inputs/Select';
import Modal from './Modals/Modal';
import TradeFormInputLabel from './TradeForm/TradeFormInputLabel';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import ExchangeHistoryClient from 'src/utils/exchangeHistoryClient';
import { PerpMarketConfig, SpotMarketConfig } from '@drift-labs/sdk';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';

export const DownloadTypes = [
	{
		label: 'Trades',
		value: 'trades',
	},
	{
		label: 'Market Trades',
		value: 'market-trades',
	},
	{
		label: 'Settle P&L Records',
		value: 'settle-pnl-records',
	},
	{
		label: 'Funding Rates',
		value: 'funding-rates',
	},
	{
		label: 'Funding Payments',
		value: 'funding-payments',
	},
	{
		label: 'Deposits',
		value: 'deposits',
	},
	{
		label: 'Liquidations',
		value: 'liquidations',
	},
	{
		label: 'BAL Records',
		value: 'lp-records',
	},
	{
		label: 'IF Stake Records',
		value: 'if-stake-records',
	},
];

export const Years = [
	{ label: '2022', value: '2022' },
	{ label: '2023', value: '2023' },
	{ label: '2024', value: '2024' },
];

export const Months = [
	{
		label: 'Jan.',
		value: '01',
	},
	{
		label: 'Feb.',
		value: '02',
	},
	{
		label: 'Mar.',
		value: '03',
	},
	{
		label: 'Apr.',
		value: '04',
	},
	{
		label: 'May',
		value: '05',
	},
	{
		label: 'Jun.',
		value: '06',
	},
	{
		label: 'Jul.',
		value: '07',
	},
	{
		label: 'Aug.',
		value: '08',
	},
	{
		label: 'Sep.',
		value: '09',
	},
	{
		label: 'Oct.',
		value: '10',
	},
	{
		label: 'Nov.',
		value: '11',
	},
	{
		label: 'Dec.',
		value: '12',
	},
];

const Days = Array(31)
	.join()
	.split(',')
	.map(
		function (_a) {
			return this.i++;
		},
		{ i: 1 }
	)
	.map((val) => {
		return {
			label: val.toString(),
			value: val.toString().padStart(2, '0'),
		};
	});

const isUserDownload = (downloadType: DownloadRecordType) =>
	[
		'trades',
		'deposits',
		'liquidations',
		'funding-payments',
		'settle-pnl-records',
		'lp-records',
	].includes(downloadType);

const isDailyDownload = (downloadType: DownloadRecordType) =>
	downloadType === 'market-trades';

const isMarketDownload = (downloadType: DownloadRecordType) =>
	downloadType === 'market-trades' || downloadType === 'funding-rates';

export const Downloader = (props: {
	recordType: DownloadRecordType;
	year: string;
	month?: string;
	day?: string;
	userDownloadAccounts?: string[];
	marketSymbol?: string;
}) => {
	const [infoMessage, setInfoMessage] = useState('');

	const [isDownloading, setIsDownloading] = useState(false);
	const accounts = useLazySubAccounts();
	const currentAuthority = useCurrentAuthority();

	const downloadIsValid = useMemo(() => {
		if (
			isUserDownload(props.recordType) &&
			(!props.userDownloadAccounts || !props.userDownloadAccounts?.length)
		) {
			setInfoMessage('Select an account');
			return false;
		}

		if (isDailyDownload(props.recordType) && !props.day) {
			setInfoMessage('Select a day');
			return false;
		}

		if (isMarketDownload(props.recordType) && !props.marketSymbol) {
			setInfoMessage('Select a market');
			return false;
		}

		return true;
	}, [props]);

	const downloadFile = async (
		accountKey?: string
	): Promise<{ success: boolean; message: string }> => {
		try {
			const result = await ExchangeHistoryClient.fetchFileDownload({
				fileType: props.recordType,
				month: props.month,
				year: props.year,
				day: props.day,
				user: accountKey,
				authority: currentAuthority?.toString(),
				marketSymbol: props.marketSymbol,
			});

			if (!result.success) {
				return { success: false, message: result.message };
			}

			const value = result.body.data.file;
			const a = window.document.createElement('a');
			a.href = window.URL.createObjectURL(
				new Blob(
					value.split(',').map((val) => val + ','),
					{ type: 'text/csv' }
				)
			);

			a.download = result.body.data.fileTitle;

			document.body.appendChild(a);
			a.click();

			document.body.removeChild(a);

			return { success: true, message: 'Success!' };
		} catch (e) {
			return {
				success: false,
				message: 'Something went wrong, please try again or message support.',
			};
		}
	};

	const iterateFiles = async () => {
		setInfoMessage('');

		if (props.userDownloadAccounts?.length > 0) {
			const results = await Promise.all(
				props.userDownloadAccounts.map((acct) => {
					const pubKey =
						accounts.find((userAcct) => userAcct.userKey == acct)?.pubKey ??
						null;
					if (pubKey) {
						return downloadFile(pubKey.toString());
					}
				})
			);

			const failureList = results.filter((result) => !result.success);

			if (failureList.length === 0) {
				setInfoMessage('Success!');
			} else if (failureList.length === 1 && results.length === 1) {
				setInfoMessage(failureList[0].message);
			} else {
				setInfoMessage(
					`${failureList.length} requested file${
						failureList.length > 1 ? 's were' : ' was'
					} unavailable.`
				);
			}
		} else {
			const result = await downloadFile();
			setInfoMessage(result.message);
		}
	};

	const actions = useDriftActions();
	const showModal = actions.showModal;
	const setLoadingBarDisplay = (display: boolean) =>
		showModal('showLoadingBar', display);

	useEffect(() => {
		setInfoMessage('');
	}, [Object.entries(props).join(',')]);

	useEffect(() => {
		setLoadingBarDisplay(isDownloading);

		return () => {
			setLoadingBarDisplay(false);
		};
	}, [isDownloading]);

	return (
		<div className="flex flex-col items-start justify-between w-full mt-2">
			<Button.Secondary
				size="MEDIUM"
				highlight
				onClick={(e) => {
					e.stopPropagation();
					setIsDownloading(true);
					iterateFiles()
						.then(() => {
							setIsDownloading(false);
						})
						.catch(() => {
							setIsDownloading(false);
						});
				}}
				disabled={isDownloading || !downloadIsValid}
				className="w-full"
			>
				Download
			</Button.Secondary>
			{infoMessage !== '' && (
				<span className="inline-flex pt-2 text-xs font-normal text-text-emphasis whitespace-nowrap">
					{infoMessage}
				</span>
			)}
		</div>
	);
};

export const DownloadModal = (props: { initialType?: DownloadRecordType }) => {
	const set = useDriftStore((s) => s.set);
	const showDownloadModal = useDriftStore((s) => s.modals.showDownloadModal);

	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const accounts = useLazySubAccounts();
	const connected = useWalletIsConnected();

	const [downloadType, setDownloadType] = useState<DownloadRecordType>(
		props.initialType ?? 'trades'
	);

	const currentDate = new Date();

	const [downloadYear, setDownloadYear] = useState(
		currentDate.getFullYear().toString()
	);
	const [downloadMonth, setDownloadMonth] = useState('01');
	const [downloadDay, setDownloadDay] = useState('01');
	const [downloadMarket, setDownloadMarket] = useState('SOL-PERP');
	const [downloadAccounts, setDownloadAccounts] = useState([currentUserKey]);

	const [accountOptions, setAccountOptions] = useState([]);
	const [defaultSelectLabel, setDefaultSelectLabel] = useState('');

	const toggleAccountChecked = (toggledKey: string) => {
		const checkedIndex = downloadAccounts.indexOf(toggledKey);
		const newAccountList = [...downloadAccounts];
		if (checkedIndex >= 0) {
			newAccountList.splice(checkedIndex, 1);
		} else {
			newAccountList.push(toggledKey);
		}
		setDownloadAccounts(newAccountList);
	};

	const selectAllOptions = (currentlyChecked: boolean) => {
		if (currentlyChecked) {
			setDownloadAccounts([]);
		} else {
			setDownloadAccounts(accounts.map((acct) => acct.userKey));
		}
	};

	useEffect(() => {
		const newDefaultLabel =
			downloadAccounts.length === 1
				? accounts.find((acct) => acct.userKey === downloadAccounts[0])?.name ??
				  ''
				: `${downloadAccounts.length} accounts selected`;

		setDefaultSelectLabel(newDefaultLabel);

		const newAcctOptions = accounts.map((acct) => {
			return {
				label: acct.name,
				value: acct.userId,
				checked: downloadAccounts.includes(acct.userKey),
				onCheck: () => toggleAccountChecked(acct.userKey),
				switch: false,
			};
		});

		const selectAllChecked =
			newAcctOptions.filter((opt) => opt.checked).length ==
			newAcctOptions.length;

		newAcctOptions.unshift({
			label: 'Select All',
			value: -1,
			checked: selectAllChecked,
			onCheck: () => selectAllOptions(selectAllChecked),
			switch: true,
		});
		setAccountOptions(newAcctOptions);
	}, [accounts?.length, downloadAccounts]);

	useEffect(() => {
		if (!connected) handleClose();
	}, [connected]);

	if (!showDownloadModal) return null;

	const handleClose = () => {
		set((s) => {
			s.modals.showDownloadModal = false;
		});
	};

	return (
		<Modal onClose={handleClose}>
			<Modal.Body overflow>
				<Modal.Header onClose={handleClose}>
					<span>
						<span className="capitalize">Download statement</span>
					</span>
				</Modal.Header>

				<Modal.Content>
					<div className="block w-full">
						{isUserDownload(downloadType) && (
							<div className="z-50 w-full py-1">
								<div className="flex flex-col">
									<TradeFormInputLabel className="mt-0">
										Account
									</TradeFormInputLabel>
									<Select.MultiSelector
										id="accountSelector"
										selection={downloadAccounts}
										options={accountOptions}
										defaultLabel={defaultSelectLabel}
										maxHeight={150}
									/>
								</div>
							</div>
						)}
						<div className="z-50 w-full py-1">
							<div className="flex flex-col">
								<TradeFormInputLabel className="mt-0">Data</TradeFormInputLabel>
								<Select.Default
									id="recordTypeSelector"
									selection={downloadType}
									options={DownloadTypes}
									onChange={(newValue) => {
										setDownloadType(newValue as DownloadRecordType);
									}}
									maxHeight={150}
								/>
							</div>
						</div>
						<div className="z-50 w-full py-1">
							<div className="flex flex-col">
								<TradeFormInputLabel className="mt-0">Year</TradeFormInputLabel>
								<Select.Default
									id="yearSelector"
									selection={downloadYear}
									options={Years}
									onChange={(newValue) => {
										setDownloadYear(newValue);
									}}
								/>
							</div>
						</div>

						<div className="w-full py-1">
							<div className="flex flex-col">
								<TradeFormInputLabel className="mt-0">
									Month
								</TradeFormInputLabel>
								<Select.Default
									id="monthSelector"
									selection={downloadMonth}
									options={Months}
									onChange={(newValue) => {
										setDownloadMonth(newValue);
									}}
									maxHeight={150}
								/>
							</div>
						</div>
						{downloadType == 'market-trades' && (
							<div className="w-full py-1">
								<div className="flex flex-col">
									<TradeFormInputLabel className="mt-0">
										Day
									</TradeFormInputLabel>
									<Select.Default
										id="daySelector"
										selection={downloadDay}
										options={Days}
										onChange={(newValue) => {
											setDownloadDay(newValue);
										}}
										maxHeight={150}
									/>
								</div>
							</div>
						)}
						{(downloadType == 'market-trades' ||
							downloadType == 'funding-rates') && (
							<div className="w-full py-1">
								<div className="flex flex-col">
									<TradeFormInputLabel className="mt-0">
										Market
									</TradeFormInputLabel>
									<Select.Default
										id="marketDownloadSelector"
										selection={downloadMarket}
										options={CurrentPerpMarkets.concat(
											//@ts-ignore
											...CurrentSpotMarkets
										).map((mkt: PerpMarketConfig | SpotMarketConfig) => {
											return { value: mkt.symbol, label: mkt.symbol };
										})}
										onChange={(newValue) => {
											setDownloadMarket(newValue);
										}}
										maxHeight={150}
									/>
								</div>
							</div>
						)}
						<div className="pt-2" />
						<Downloader
							recordType={downloadType}
							year={downloadYear}
							month={downloadMonth}
							day={isDailyDownload(downloadType) ? downloadDay : undefined}
							userDownloadAccounts={
								isUserDownload(downloadType) ? downloadAccounts : undefined
							}
							marketSymbol={
								isMarketDownload(downloadType) ? downloadMarket : undefined
							}
						/>
					</div>
				</Modal.Content>
			</Modal.Body>
		</Modal>
	);
};
